import * as React from "react";
import { isLovableEditor, useSafeRendering } from "./environment";

/**
 * Safely handle dynamic imports in a way that works consistently
 * in both development and production environments
 * 
 * @param moduleId The module path to import
 * @returns Promise that resolves to the imported module
 */
export async function safeImport(moduleId: string): Promise<any> {
  // Log import attempt for debugging
  console.log(`Importing module: ${moduleId}`);
  
  // Handle Lovable editor environment paths
  if (isLovableEditor()) {
    moduleId = normalizeLovablePath(moduleId);
  }
  
  try {
    // Use direct import with the original path
    // The @vite-ignore comment tells Vite not to analyze this import
    return await import(/* @vite-ignore */ moduleId);
  } catch (error) {
    console.error(`Error importing module ${moduleId}:`, error);
    
    // Try with absolute path for production
    if (process.env.NODE_ENV === 'production' && !moduleId.startsWith('/')) {
      try {
        console.log(`Retrying import with absolute path: /${moduleId}`);
        return await import(/* @vite-ignore */ `/${moduleId}`);
      } catch (retryError) {
        console.error(`Retry failed for module ${moduleId}:`, retryError);
      }
    }
    
    // Try with explicit file extension if not provided
    if (!moduleId.endsWith('.js') && !moduleId.endsWith('.ts') && !moduleId.endsWith('.tsx')) {
      try {
        console.log(`Retrying import with explicit extension: ${moduleId}.js`);
        return await import(/* @vite-ignore */ `${moduleId}.js`);
      } catch (retryError) {
        console.error(`Retry with explicit extension failed for module ${moduleId}:`, retryError);
      }
    }
    
    // Special handling for Lovable editor - try removing @ prefix
    if (moduleId.startsWith('@/') && isLovableEditor()) {
      const withoutPrefix = moduleId.replace('@/', '/src/');
      try {
        console.log(`Lovable editor: trying path without @ prefix: ${withoutPrefix}`);
        return await import(/* @vite-ignore */ withoutPrefix);
      } catch (prefixError) {
        console.error(`Prefix removal failed for ${moduleId}:`, prefixError);
      }
    }
    
    // Try with CDN fallback for critical components
    if (moduleId.includes('/components/') || moduleId.includes('/pages/')) {
      const componentName = moduleId.split('/').pop()?.split('.')[0];
      if (componentName) {
        try {
          console.log(`Attempting CDN fallback for ${componentName}`);
          const cdnUrl = `/assets/${componentName}-*.js`;
          // This is a simplified approach - in a real implementation,
          // you would need a more sophisticated matching mechanism
          return await import(/* @vite-ignore */ cdnUrl);
        } catch (cdnError) {
          console.error(`CDN fallback failed for ${componentName}:`, cdnError);
        }
      }
    }
    
    // NEW: Add specific retry logic for known problematic components
    if (moduleId.includes('fullcalendar') || moduleId.includes('service-details')) {
      try {
        console.log(`Using special handling for ${moduleId}`);
        // Try with a slight delay to allow for other dependencies to load first
        await new Promise(resolve => setTimeout(resolve, 50));
        return await import(/* @vite-ignore */ moduleId);
      } catch (specialHandlingError) {
        console.error(`Special handling failed for ${moduleId}:`, specialHandlingError);
      }
    }
    
    throw error;
  }
}

/**
 * Convert paths to work correctly in Lovable editor environment
 */
function normalizeLovablePath(path: string): string {
  // Handle alias paths in Lovable editor
  if (path.startsWith('@/')) {
    // In Lovable, @/ may need to be /src/
    return path.replace('@/', '/src/');
  }
  
  // Handle relative paths in Lovable editor
  if (path.startsWith('./')) {
    return path.replace('./', '/src/');
  }
  
  return path;
}

/**
 * Preload a module without executing it immediately
 * This helps improve performance for anticipated navigations
 * 
 * @param moduleId The module path to preload
 * @returns Promise that resolves when preloading is complete
 */
export function preloadModule(moduleId: string): Promise<void> {
  return new Promise((resolve) => {
    console.log(`Preloading module: ${moduleId}`);
    
    // Create a link preload for the module
    if (document && moduleId.endsWith('.js')) {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'script';
      link.href = moduleId;
      document.head.appendChild(link);
    }
    
    // Also try to import the module
    safeImport(moduleId)
      .then(() => {
        console.log(`Successfully preloaded: ${moduleId}`);
        resolve();
      })
      .catch((error) => {
        console.warn(`Preloading module ${moduleId} failed:`, error);
        resolve(); // Resolve anyway to not block execution chain
      });
  });
}

/**
 * Generate a consistent chunk name for code splitting
 * 
 * @param name A descriptive name for the chunk
 * @returns A formatted chunk name
 */
export function getChunkName(name: string): string {
  // Remove special characters and convert to kebab case
  return name
    .replace(/[^a-zA-Z0-9]/g, '-')
    .toLowerCase();
}

/**
 * Determine if the current environment is a production build
 */
export function isProductionBuild(): boolean {
  return process.env.NODE_ENV === 'production';
}

/**
 * Get a module path that works reliably in the current environment
 */
export function getEnvironmentAwarePath(path: string): string {
  // If path is already absolute, return as is
  if (path.startsWith('/') || path.includes('://')) {
    return path;
  }
  
  // For Lovable editor, use absolute paths
  if (isLovableEditor()) {
    if (path.startsWith('@/')) {
      return path.replace('@/', '/src/');
    }
    return path.startsWith('./') ? path.substring(2) : path;
  }
  
  // For production, use absolute paths
  if (isProductionBuild()) {
    return path.startsWith('./') ? path.substring(2) : path;
  }
  
  // For development, return the path as-is
  return path;
}

/**
 * Create a more reliable dynamic import function for components
 * with special handling for Lovable editor environment
 * 
 * @param componentPath The path to the component
 * @returns A dynamic import function that handles various scenarios
 */
export function createDynamicImport(componentPath: string) {
  return () => {
    console.log(`Creating dynamic import for: ${componentPath}`);
    
    // Skip lazy loading for critical components in Lovable editor
    if (useSafeRendering() && isLovableEditorCriticalComponent(componentPath)) {
      console.log(`Using direct import for critical component in Lovable editor: ${componentPath}`);
      // For critical components, we'll try to return a placeholder in case of errors
      try {
        // Try to resolve synchronously by returning a stub component
        return Promise.resolve({
          default: ({ children }: any) => React.createElement('div', { 
            className: 'p-4 border border-blue-200 bg-blue-50 rounded-md'
          }, 
            React.createElement('p', { className: 'font-medium mb-1' }, `Component: ${componentPath.split('/').pop()}`),
            children
          )
        });
      } catch (err) {
        console.warn(`Failed to create placeholder for ${componentPath}:`, err);
      }
    }
    
    // For normal cases, use safe import
    return safeImport(getEnvironmentAwarePath(componentPath))
      .catch(err => {
        console.error(`Failed to load component ${componentPath}:`, err);
        // Return a minimal component that won't break the app
        return { 
          default: ({ children }: any) => React.createElement('div', { 
            className: 'p-4 border border-red-200 bg-red-50 text-red-700 rounded-md'
          },
            React.createElement('p', { className: 'font-medium' }, `Failed to load component: ${componentPath.split('/').pop()}`),
            React.createElement('p', { className: 'text-sm opacity-80 mt-1' }, err.message),
            children ? React.createElement('div', { className: 'mt-2 p-2 border border-red-100 rounded bg-white' }, children) : null
          )
        };
      });
  };
}

/**
 * Determine if a component is critical and shouldn't be lazy loaded in Lovable editor
 */
function isLovableEditorCriticalComponent(path: string): boolean {
  const criticalPaths = [
    'dashboard',
    'admin',
    'calendar',
    'bookings',
    'service-details',
    'chart'
  ];
  
  return criticalPaths.some(criticalPath => path.toLowerCase().includes(criticalPath));
}

/**
 * Preload calendar components for specific routes
 * @param route The current route path
 * @returns Promise that resolves when preloading is complete
 */
export function preloadCalendarComponents(route?: string): Promise<void[]> {
  // Only preload for service details and dashboard routes
  if (route && !route.includes('/service/') && !route.includes('/dashboard')) {
    console.log('Skipping calendar preload for non-calendar route:', route);
    return Promise.resolve([]);
  }

  // For service details page, we don't need to preload since components are directly imported
  if (route && route.includes('/service/') && !route.includes('/dashboard')) {
    console.log('Skipping calendar preload for service details - using direct imports');
    return Promise.resolve([]);
  }

  console.log('Preloading calendar components for dashboard route:', route);
  
  // Only preload components needed for the dashboard
  const calendarComponents = [
    '@/components/admin/calendar/CalendarSection',
    '@/components/admin/calendar/TimeSlotSection',
    '@/hooks/calendar/useAdminCalendar'
  ];

  return Promise.all(
    calendarComponents.map(moduleId => 
      preloadModule(moduleId).catch(error => {
        console.warn(`Failed to preload calendar component ${moduleId}:`, error);
        // Don't reject the promise to prevent blocking other components
        return Promise.resolve();
      })
    )
  );
}

/**
 * Preload chart-related components and dependencies
 * Should be called when navigating to pages with charts
 */
export function preloadChartComponents(): Promise<void[]> {
  // Try to eagerly load recharts and its dependencies
  const criticalPackages = [
    'recharts',
    'd3-shape',
    'd3-scale',
    'd3-path',
    'd3-array'
  ];
  
  return Promise.all([
    ...criticalPackages.map(pkg => {
      // Use a setTimeout to stagger loading and reduce network contention
      return new Promise<void>(resolve => {
        setTimeout(() => {
          import(/* @vite-ignore */ pkg)
            .then(() => {
              console.log(`Successfully preloaded ${pkg}`);
              resolve();
            })
            .catch(error => {
              console.warn(`Failed to preload ${pkg}:`, error);
              resolve(); // Resolve anyway to not block execution chain
            });
        }, Math.random() * 200); // Stagger loading
      });
    }),
    
    // Also preload our chart components
    preloadModule('@/components/ui/lazy-chart'),
    preloadModule('@/components/admin/dashboard/SafeDashboardCharts')
  ]);
}

/**
 * Create an error boundary wrapper for dynamic imports with enhanced Lovable editor support
 * 
 * @param importFn The dynamic import function
 * @param fallback A fallback component to render on error
 * @returns A component that handles loading errors
 */
export function createErrorBoundaryImport(importFn: () => Promise<any>, fallback?: React.ComponentType) {
  // In Lovable editor, use a more reliable approach for critical components
  if (useSafeRendering() && typeof importFn === 'function') {
    // Extract component name from function toString for better error messages
    const fnString = importFn.toString();
    const match = fnString.match(/['"]@\/components\/([^'"]+)['"]/);
    const componentName = match ? match[1] : 'Unknown';
    
    console.log(`Using safe loading for component: ${componentName}`);
  }
  
  return React.lazy(() => 
    importFn().catch(err => {
      console.error("Failed to load component:", err);
      
      if (fallback) {
        return { default: fallback };
      }
      
      // Default fallback component
      return {
        default: (props: any) => React.createElement('div', {
          className: 'p-4 border border-red-200 bg-red-50 text-red-700 rounded-md'
        },
          React.createElement('p', { className: 'font-medium mb-2' }, 'Failed to load component'),
          React.createElement('p', { className: 'text-sm opacity-80' }, err.message),
          props.children ? React.createElement('div', { className: 'mt-2 p-2 bg-white rounded' }, props.children) : null
        )
      };
    })
  );
}
